import React, { useEffect, useState } from 'react';
import { Input, Button, Dropdown } from 'components/atoms/index';
import { Icon } from '@/icons';
import AcademyStudentTable from 'components/organisms/Table/AcademyStudentTable';
import { adminAcademyApi, academyStudentApi, adminStudentApi, academyClassApi } from '@/services/api';
import { useSessionSlug } from '@/hooks/useSessionSlug';

interface StudentData {
  id: string;
  userId?: string; // userId 필드 추가
  name: string;
  email: string;
  grade: string;
  status: 'active' | 'inactive';
  registrationDate: string;
  academyCode: string;
  birthDate: string;
  className?: string;
}

interface StudentSelectionContentProps {
  onOk: (selectedStudents: StudentData[]) => void;
  onCancel: () => void;
  availableStudents?: StudentData[];
  title?: string;
  selectionMode?: 'single' | 'multiple';
  useAdminApi?: boolean;
  showAcademyCode?: boolean;
  showClassName?: boolean;
}

const StudentSelectionContent: React.FC<StudentSelectionContentProps> = ({
  onOk,
  onCancel,
  availableStudents,
  title = "학생 선택",
  selectionMode = 'multiple',
  useAdminApi = false,
  showAcademyCode = true,
  showClassName = false
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedStudents, setSelectedStudents] = useState<StudentData[]>([]);
  const [academies, setAcademies] = useState<{ value: string; label: string }[]>([]);
  const [selectedAcademy, setSelectedAcademy] = useState<string>('');
  // const [classes, setClasses] = useState<{ value: number; label: string }[]>([]);
  // const [selectedClassId, setSelectedClassId] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [students, setStudents] = useState<StudentData[]>(availableStudents || []);
  const externalData = !!availableStudents && (availableStudents.length > 0);

  // 검색어 변경 시 페이지를 1로 리셋
  useEffect(() => {
    if (!externalData) {
      setPage(1);
    }
  }, [searchTerm, externalData]);

  const { academyCode: sessionAcademyCode } = useSessionSlug();
  const defaultAcademyCode = sessionAcademyCode || '';

  // 클래스 목록 로딩 (학원 모드일 때만) - 사용하지 않음
  // useEffect(() => {
  //   if (useAdminApi || !defaultAcademyCode) return;
  //   (async () => {
  //     try {
  //       const res = await academyClassApi.list({ academyCode: defaultAcademyCode, page: 0, size: 100 });
  //       const data = res?.data || res;
  //       const content = data?.content || [];
  //       setClasses(content.map((c: any) => ({ value: c.id, label: c.name })));
  //     } catch (_) {}
  //   })();
  // }, [defaultAcademyCode, useAdminApi]);

  // 외부에서 학생 리스트를 제공받은 경우: 해당 데이터로 페이징/검색만 처리
  useEffect(() => {
    if (externalData) {
      setStudents(availableStudents || []);
      setTotal((availableStudents || []).length);
      return;
    }
    (async () => {
      try {
        const res = await adminAcademyApi.list({ q: '', contractStatus: undefined, page: 0, size: 100 });
        const data = res?.data || res; const pageData = data?.data || data; const content = pageData?.content || [];
        const opts = content.map((a: any) => ({ value: a.code || a.academyCode || a.id || a.name, label: a.name || a.academyName }));
        setAcademies(opts);
        if (!useAdminApi) {
          setSelectedAcademy(defaultAcademyCode || (opts[0]?.value || ''));
        }
      } catch (_) {}
    })();
  }, [externalData, availableStudents, defaultAcademyCode, useAdminApi]);

  useEffect(() => {
    if (externalData) return;
    (async () => {
      try {
        let res;
        if (useAdminApi) {
          res = await adminStudentApi.listAll({
            page: page - 1,
            size: pageSize,
            q: searchTerm,
            academyCode: selectedAcademy || undefined
          });
        } else {
          if (!selectedAcademy) return;
          res = await academyStudentApi.list({ 
            academyCode: selectedAcademy, 
            q: searchTerm, 
            page: page - 1, 
            size: pageSize,
            // classId: selectedClassId
          });
        }
        const data = res?.data || res; const pageData = data?.data || data;
        const content = pageData?.content || [];
        const mapped: StudentData[] = content.map((s: any) => ({
          id: String(s.userId || s.studentUserId || s.academyStudentId || s.id),
          userId: String(s.userId || s.studentUserId || s.academyStudentId || s.id),
          name: s.name || s.displayName || s.studentName,
          loginId: s.studentLoginId || s.loginId || s.userLoginId || s.studentId || '',
          email: s.email || '',
          grade: s.grade || '',
          status: 'active',
          registrationDate: s.registrationDate || s.enrolledAt || '',
          academyCode: s.academyCode || selectedAcademy || '',
          className: s.className || '',
          birthDate: s.birthDate || s.birth || ''
        }));
        setStudents(mapped);
        setTotal(pageData?.totalElements || mapped.length);
      } catch (_) {}
    })();
  }, [externalData, selectedAcademy, page, pageSize, searchTerm, useAdminApi]);

  const handleStudentSelect = (student: StudentData) => {
    if (selectionMode === 'single') {
      const already = selectedStudents.find(s => s.id === student.id);
      setSelectedStudents(already ? [] : [student]);
      return;
    }
    const isSelected = selectedStudents.find(s => s.id === student.id);
    if (isSelected) {
      setSelectedStudents(selectedStudents.filter(s => s.id !== student.id));
    } else {
      setSelectedStudents([...selectedStudents, student]);
    }
  };

  const handleOk = () => {
    if (selectedStudents.length === 0) {
      alert('학생을 선택해주세요.');
      return;
    }
    onOk(selectedStudents);
    setSelectedStudents([]);
    setSearchTerm('');
  };

  const handleCancel = () => {
    setSelectedStudents([]);
    setSearchTerm('');
    onCancel();
  };

  return (
    <div className='p-5 space-y-6'>
      <div className='mb-6'>
        <h3 className='text-xl font-bold text-gray-900'>{title}</h3>
      </div>

      {useAdminApi && (
        <div className="flex gap-3 items-center mb-2">
          <Dropdown
            options={[{ value: '', label: '전체 학원' }, ...academies]}
            value={selectedAcademy}
            onChange={(val) => setSelectedAcademy(val)}
            placeholder="학원을 선택하세요"
            className="max-w-[260px]"
          />
        </div>
      )}

      {/* 클래스 선택 (학원 모드일 때) - 삭제됨
      {!useAdminApi && classes.length > 0 && (
        <div className="flex gap-3 items-center mb-2">
           <Dropdown
            options={[{ value: '', label: '전체 클래스' }, ...classes.map(c => ({ value: String(c.value), label: c.label }))]}
            value={selectedClassId ? String(selectedClassId) : ''}
            onChange={(val) => setSelectedClassId(val ? Number(val) : undefined)}
            placeholder="클래스 선택"
            className="max-w-[200px]"
          />
        </div>
      )}
      */}

      {/* 선택된 학생 수 */}
      {/* <div className='text-sm text-gray-600'>
        선택된 학생수: {selectedStudents.length}명
      </div> */}

      {/* 학생 테이블 */}
      <AcademyStudentTable
        data={students}
        selectedStudents={selectedStudents}
        onStudentSelect={handleStudentSelect}
        onSelectAll={(students) => {
          if (selectionMode === 'single') {
            setSelectedStudents(students.length > 0 ? [students[0]] : []);
          } else {
            setSelectedStudents(students);
          }
        }}
        type="selection"
        showSearch={true}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showPagination={true}
        currentPage={page}
        pageSize={pageSize}
        total={total}
        onPageChange={(p) => setPage(p)}
        showAcademyCode={showAcademyCode}
        showClassName={showClassName}
        searchPlaceholder={showAcademyCode ? '학생명, 클래스명 또는 학원코드로 검색' : '학생명 또는 클래스명으로 검색'}
      />

      {/* 버튼 영역 */}
      <div className="flex gap-2 justify-end pt-4">
        <Button
          intent="secondary"
          onClick={handleCancel}
          className="min-w-[100px]"
        >
          취소
        </Button>
        <Button
          onClick={handleOk}
          className="min-w-[100px]"
        >
          선택
        </Button>
      </div>
    </div>
  );
};

export default StudentSelectionContent; 